import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Flame, ChevronRight, Stethoscope, Leaf, TriangleAlert } from 'lucide-react';
import { RecommendationProps } from './RecommendationCard';
import { getBenefitIcon } from '@/constants/ui';

interface SupplementItemProps {
  supplement: RecommendationProps;
  isSelected: boolean;
  onClick: () => void;
  selectedExists: boolean;
}

const SupplementItem: React.FC<SupplementItemProps> = ({ supplement, isSelected, onClick, selectedExists }) => {
  // Function to get benefit description for tooltips
  const getBenefitDescription = (benefit: string): string => {
    return supplement.benefitsLong?.[benefit] || `${benefit} may provide health benefits based on clinical studies.`;
  };
  
  const priorityText = {
    high: <span>This is a <strong>top recommendation</strong> based on your profile; it's going to be one of the <strong>most effective supplements</strong> for your needs.</span>,
    medium: <span>We'd <strong>strongly recommend</strong> this based on your profile, and you're likely to see a <strong>noticeable difference.</strong></span>,
    low: <span>This is a <strong>beneficial supplement</strong> to consider, but it <strong>may not be as effective for your needs</strong> as the other recommendations.</span>
  };

  return (
    <Card 
      onClick={onClick}
      className={cn(
        'p-4 cursor-pointer transition-all duration-200 rounded-lg mx-2 my-4 border hover:shadow-md relative overflow-hidden',
        isSelected 
          ? 'bg-white shadow-md before:absolute before:inset-0  before:pointer-events-none before:rounded-lg border-primary/70' 
          : 'bg-white/40 hover:border-primary/70 hover:opacity-100',
        !isSelected && selectedExists && 'opacity-100'
      )}>

      <div className="flex items-start justify-between">
        <h3 className="text-lg font-medium">
          {supplement.name}
        </h3>
        <div className="flex items-center gap-2">
          {supplement.priority === 'high' && (
            <Badge variant="outline" className="border-amber-300 text-amber-500 py-1 px-3">
              <Flame className="h-3 w-3" />
            </Badge>
          )}
          {supplement.type && (
            <Badge variant="outline" className={cn(
              "text-xs py-0 px-2 capitalize flex items-center gap-1",
              supplement.type.toLowerCase() === 'clinical' 
                ? 'border-green-300 text-green-700' 
                : (supplement.type.toLowerCase() === 'herbal' 
                  ? 'border-amber-500 text-amber-700'
                  : 'border-red-300 text-red-700')
            )}>
              {supplement.type.toLowerCase() === 'clinical' && <Stethoscope className="h-3 w-3 mr-0.5" />}
              {supplement.type.toLowerCase() === 'herbal' && <Leaf className="h-3 w-3 mr-0.5" />}
              {supplement.type.toLowerCase() !== 'clinical' && supplement.type.toLowerCase() !== 'herbal' && <TriangleAlert className="h-3 w-3 mr-0.5" />}
              {supplement.type}
            </Badge>
          )}
          <ChevronRight className={cn(
            "h-4 w-4 transition-all duration-200",
            isSelected ? "text-primary" : "text-gray-300"
          )}/>
        </div>
      </div>
      
      <p className="text-sm text-muted-foreground mt-1">
        {supplement.description}
      </p>
      
      {/*<div className="flex flex-wrap gap-1.5 mt-2">
        <TooltipProvider>
          {supplement.benefits.slice(0, 4).map((benefit, index) => {
            const iconInfo = getBenefitIcon(benefit);
            return (
              <Tooltip key={index} delayDuration={0}>
                <TooltipTrigger asChild>
                  <Badge 
                    variant="outline" 
                    className={cn(
                      "flex items-center gap-1.5 font-normal text-xs cursor-help hover:bg-black/5",
                      iconInfo.className.replace('text-', 'border-')
                    )}
                  >
                    {React.cloneElement(iconInfo.icon as React.ReactElement, {
                      className: `h-3 w-3 ${iconInfo.className}`
                    })}
                    {benefit}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[250px] p-3">
                  <div className="text-sm" dangerouslySetInnerHTML={{ __html: getBenefitDescription(benefit) }} />
                </TooltipContent>
              </Tooltip>
            );
          })}
          
          {supplement.benefits.length > 4 && (
            <Badge variant="outline" className="text-xs">
              +{supplement.benefits.length - 4} more
            </Badge>
          )}
        </TooltipProvider>
      </div>*/}
      
      <div className="mt-4 pt-3 border-t text-xs text-muted-foreground">
        {priorityText[supplement.priority]}
      </div>
    </Card>
  );
};

export default SupplementItem; 